import {
	EDITORS,
	focusEditor,
	navigationStep,
	type RowStop,
	stepEditor,
	stepEditorByRows,
	stepEditorVertically,
	verticalStep,
} from "@/shared/utils/cellFocus";
import {
	type KeyboardEvent,
	type MouseEvent,
	useEffect,
	useRef,
	useState,
} from "react";

export type CellPosition = { row: number; cell: number };

export const cellPositionOf = (target: EventTarget): CellPosition | null => {
	const cell = (target as HTMLElement).closest("td");
	const row = cell?.parentElement;

	return cell && row instanceof HTMLTableRowElement
		? { row: row.sectionRowIndex, cell: cell.cellIndex }
		: null;
};

const cellAt = (root: HTMLElement, position: CellPosition) => {
	const row =
		root instanceof HTMLTableRowElement
			? root
			: root instanceof HTMLTableSectionElement
				? root.rows[position.row]
				: undefined;

	return row?.cells[position.cell];
};

type Options = {
	isEditing: boolean;
	isLocked?: boolean;
	canStartEdit?: boolean;
	blockedReason?: string | null;
	initialCell?: CellPosition | null;
	onEdit?: (cell: CellPosition | null) => void;
	onCancel?: () => void;
	onSave?: () => void;
	onStepPastEnd?: () => boolean;
};

export const useEditableRow = <T extends HTMLElement>({
	isEditing,
	isLocked = false,
	canStartEdit = true,
	blockedReason = null,
	initialCell = null,
	onEdit = () => {},
	onCancel = () => {},
	onSave = () => {},
	onStepPastEnd,
}: Options) => {
	const ref = useRef<T>(null);
	const clickedCellRef = useRef<CellPosition | null>(initialCell);
	const lastStopRef = useRef<RowStop | null>(null);
	const [blockedAt, setBlockedAt] = useState<number | null>(null);

	const save = () => {
		if (blockedReason === null) onSave();
		else setBlockedAt(Date.now());
	};

	const startEdit = (cell: CellPosition | null = null) => {
		clickedCellRef.current = cell;
		onEdit(cell);
	};

	const stepWithin = (target: HTMLElement, step: -1 | 1) => {
		const root = ref.current;
		if (!(root instanceof HTMLTableSectionElement) || root.rows.length < 2)
			return stepEditor(root, target, step);

		lastStopRef.current = stepEditorByRows(
			root,
			target,
			step,
			lastStopRef.current,
		);
		return lastStopRef.current !== null;
	};

	const move = (target: HTMLElement, step: -1 | 1) => {
		if (stepWithin(target, step)) return true;

		const isLastEditor =
			step === 1 &&
			ref.current?.contains(target) === true &&
			target.matches(EDITORS);

		return isLastEditor && onStepPastEnd !== undefined && onStepPastEnd();
	};

	const onKeyDownCapture = (event: KeyboardEvent<HTMLElement>) => {
		if (!isEditing) return;

		const vertical = verticalStep(event);
		if (vertical !== null) {
			if (
				stepEditorVertically(ref.current, event.target as HTMLElement, vertical)
			) {
				event.preventDefault();
				event.stopPropagation();
			}
			return;
		}

		if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

		const step = navigationStep(event);
		if (step !== null && move(event.target as HTMLElement, step))
			event.preventDefault();
	};

	const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
		if (!isEditing || event.defaultPrevented) return;

		const target = event.target as HTMLElement;

		if (event.key === "Escape") {
			event.preventDefault();
			onCancel();
			return;
		}

		if (event.key === "Enter") {
			if (event.ctrlKey || event.metaKey) {
				event.preventDefault();
				save();
				return;
			}
			if (move(target, 1)) event.preventDefault();
			return;
		}

		const step = navigationStep(event);
		if (step !== null && move(target, step)) event.preventDefault();
	};

	useEffect(() => {
		if (!isEditing) return;

		const root = ref.current;
		const clicked = clickedCellRef.current;
		clickedCellRef.current = null;
		if (!root) return;

		focusEditor(
			(clicked && cellAt(root, clicked)?.querySelector<HTMLElement>(EDITORS)) ??
				root.querySelector<HTMLElement>(EDITORS),
		);
	}, [isEditing]);

	useEffect(() => {
		if (blockedAt === null) return;
		const timer = setTimeout(() => setBlockedAt(null), 4000);
		return () => clearTimeout(timer);
	}, [blockedAt]);

	return {
		save,
		startEdit,
		canStartEdit,
		onKeyDown,
		isReasonShown: blockedAt !== null && blockedReason !== null,
		rowProps: {
			ref,
			onKeyDownCapture,
			onKeyDown,
			onMouseDown: (event: MouseEvent<HTMLElement>) => {
				if (isLocked) event.preventDefault();
			},
			onDoubleClick: (event: MouseEvent<HTMLElement>) => {
				if (isEditing || !canStartEdit) return;
				startEdit(cellPositionOf(event.target));
			},
		},
	};
};
